import type { DomainGuide } from '@/components/guide/guide-types';

import th from './th.json';
import en from './en.json';
import zh from './zh.json';
import vi from './vi.json';
import km from './km.json';
import lo from './lo.json';
import my from './my.json';

/**
 * Help catalog for the `tenancy` domain — the two tabs of `/organizations`
 * plus the two tabs inside a node's detail panel.
 *
 * The PT and credit panels live in `pages/mlm/` and are served by
 * svc-commission, but an operator only ever reaches them by selecting a node
 * on this screen, so they are documented here rather than as a separate
 * domain. They genuinely belong to the same object: `commission.mlm_nodes` is
 * an id-preserving mirror of `tenancy.organizations` (its `id` column is
 * deliberately not `defaultRandom`), so the tree row and its PT rows join by
 * node id. The mirror is fed by a message consumer, so a node created seconds
 * ago can 404 on these two tabs until the event is drained.
 *
 * What does NOT join here is the partner world under `/affiliate`. Those rows
 * carry their own ids and their own parent chain and are reached only through
 * `agent_id`; editing a rate there changes nothing here, and vice versa.
 *
 * PT is stored as integer basis points (0..10000) at every site — the live
 * row, the audit row, and the credit-movement row. Percent exists only on
 * screen.
 */
export const tenancyGuide: DomainGuide = {
  domain: 'tenancy',
  pages: [
    {
      id: 'tenancy.nodes',
      route: '/organizations (tab: Network)',
      permission: 'tenancy:read',
      status: 'live',
      fields: [
        { key: 'kind', kind: 'status' },
        { key: 'name', kind: 'text' },
        { key: 'chain', kind: 'text' },
        { key: 'prefix', kind: 'text' },
        { key: 'currency', kind: 'text' },
        { key: 'status', kind: 'status' },
        { key: 'subdomain', kind: 'text' },
        { key: 'customDomain', kind: 'text' },
        { key: 'frontend', kind: 'text' },
      ],
      cautionCount: 6,
      warningCount: 0,
    },
    {
      id: 'tenancy.frontends',
      route: '/organizations (tab: Frontends)',
      permission: 'tenancy:read',
      status: 'records-intent',
      fields: [
        { key: 'code', kind: 'text' },
        { key: 'name', kind: 'text' },
        { key: 'baseUrl', kind: 'text' },
        { key: 'version', kind: 'text' },
        { key: 'status', kind: 'status' },
      ],
      cautionCount: 4,
      warningCount: 0,
    },
    {
      id: 'tenancy.nodePt',
      route: '/organizations (Network tab → select a node → PT per provider)',
      permission: 'mlm:node_read',
      status: 'partially-wired',
      fields: [
        { key: 'effectivePt', kind: 'percent' },
        { key: 'ownPt', kind: 'percent' },
        { key: 'parentCap', kind: 'percent' },
        { key: 'enabled', kind: 'status' },
        { key: 'reason', kind: 'text' },
      ],
      cautionCount: 6,
      warningCount: 2,
    },
    {
      id: 'tenancy.nodeCredit',
      route: '/organizations (Network tab → select a node → Credit history)',
      permission: 'mlm:credit_read',
      status: 'live',
      fields: [
        { key: 'balance', kind: 'money-signed' },
        { key: 'date', kind: 'date' },
        { key: 'kind', kind: 'status' },
        { key: 'amount', kind: 'money-signed' },
        { key: 'balanceAfter', kind: 'money-signed' },
        { key: 'actor', kind: 'text' },
        { key: 'reason', kind: 'text' },
      ],
      cautionCount: 5,
      warningCount: 1,
    },
  ],
  copy: { th, en, zh, vi, km, lo, my },
};
